import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkCitations from "../lib/remarkCitations";
import CitationChip from "./CitationChip";
import "./Markdown.css";

// Task 2.2: replaced `marked` + `dangerouslySetInnerHTML` (no sanitizer) with
// react-markdown — React elements, no raw HTML string — plus rehype-sanitize.
// Answer text derives from attacker-controlled web pages (§0.4), so this is
// on the critical security path, not cosmetic.
//
// The default schema strips `className`, which would kill every hljs-*
// class from rehype-highlight (breaking code highlighting, task 9.8) and any
// citation-chip attribute (breaking task 5.11's `sup[data-n]` chips). Spelled
// out explicitly per PLAN_V2 §2.2 rather than trusting the default.
const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "sup"],
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code ?? []),
      ["className", /^language-/, /^hljs/],
    ],
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ["className", /^hljs/],
    ],
    sup: ["className", "dataN"],
    a: [...(defaultSchema.attributes?.a ?? []), "target", "rel"],
  },
  // script/iframe/object/embed/style are already absent from defaultSchema's
  // tagNames allowlist (rehype-sanitize is allowlist-based, not denylist), and
  // every `on*` attribute is likewise never in the allowlist above.
} as typeof defaultSchema;

interface Props {
  content: string;
  /** Task 5.11 — fired when a "[n]" citation chip is clicked/activated. */
  onCitationSelect?: (n: number) => void;
}

/** Renders markdown with code highlighting and a sanitized HTML tree. Also renders inline images. */
export default function Markdown({ content, onCitationSelect }: Props) {
  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkCitations]}
        rehypePlugins={[rehypeRaw, rehypeHighlight, [rehypeSanitize, schema]]}
        components={{
          a: ({ node, ...props }) => {
            const href = props.href ?? "";
            const safeHref = /^javascript:/i.test(href) ? undefined : href;
            return (
              <a {...props} href={safeHref} target="_blank" rel="noopener noreferrer nofollow" />
            );
          },
          sup: ({ node, ...props }) => <CitationChip {...(props as any)} onSelect={onCitationSelect} />,
        }}
      >
        {content || ""}
      </ReactMarkdown>
    </div>
  );
}
